/**
 * Stores > Store tablets at /stores/tablets.
 *
 * The IT register for the back-of-house tablets that run The Sweet Spot as their
 * main screen. A registered tablet holds its own long-lived device session, so
 * store staff never sign in and never need the shared store Google password.
 *
 * Four actions:
 *   - Register a tablet, which returns a one-time enrollment link. The link is
 *     shown once here and nowhere else, so the operator copies it to the tablet
 *     and opens it there. There is no way to retrieve it afterwards.
 *   - Reissue a link for a tablet that was factory reset in the field.
 *   - Revoke a tablet, which the API enforces on that device's next request
 *     because it re-reads the row rather than trusting the cookie.
 *   - Export one-time managed-app bootstrap tokens for selected active tablets.
 *
 * Gated by stores:admin. Columns sort via the shared SortableHeader with
 * URL-encoded ?sort=col:asc|desc per the grid standards (COCKPIT.md 2.7), and
 * the health filter lists most-severe-first per the filter-ordering standard.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toDataURL } from 'qrcode';
import { Check, Copy, Download, Loader2, RotateCcw, TabletSmartphone, Ban } from 'lucide-react';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { SortableHeader, gridTheadClassName, Badge, timeAgo } from '@ampersandbrands/ui';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import {
  useKioskDevices,
  useCreateKioskDevice,
  useManagedKioskBootstrap,
  useReissueKioskEnrollment,
  useRevokeKioskDevice,
  useScorecard,
  isNotAvailable,
  denverToday,
  type KioskDeviceRow,
  type ManagedKioskBootstrapResult,
  storeLabel,
} from '@/lib/useStores';
import { StoresHeader, AccessDeniedCard, NotAvailableCard, ErrorCard } from './storesShared';

const SORT_COLUMNS = ['store', 'label', 'status', 'last_seen'] as const;
type SortColumn = (typeof SORT_COLUMNS)[number];

function parseSortParam(raw: string | null): { column: SortColumn; dir: 'asc' | 'desc' } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!SORT_COLUMNS.includes(col as SortColumn)) return null;
  return { column: col as SortColumn, dir: dir === 'desc' ? 'desc' : 'asc' };
}

function sortDevices(
  rows: KioskDeviceRow[],
  column: SortColumn,
  dir: 'asc' | 'desc',
): KioskDeviceRow[] {
  const key = (d: KioskDeviceRow): string => {
    if (column === 'store') return `${d.store_name ?? ''} ${d.store_no}`.trim().toLowerCase();
    if (column === 'label') return (d.label ?? d.serial ?? '').toLowerCase();
    if (column === 'last_seen') return d.last_seen_at ?? '';
    return d.status;
  };
  const sorted = [...rows].sort((a, b) => key(a).localeCompare(key(b)));
  return dir === 'desc' ? sorted.reverse() : sorted;
}

/** "last seen" at a glance, phrased by the shared relative-time helper. */
function sinceLabel(iso: string | null): string {
  if (!iso) return 'never';
  return timeAgo(iso) || 'never';
}

function csvCell(value: string | number | null): string {
  const text = value === null ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function downloadManagedBootstrap(result: ManagedKioskBootstrapResult): void {
  const lines = [
    ['Serial_No', result.configuration_key, 'store_no', 'tablet_label', 'expires_at'],
    ...result.devices.map((device) => [
      device.serial,
      device.token,
      device.store_no,
      device.label,
      device.expires_at,
    ]),
  ].map((row) => row.map(csvCell).join(','));
  const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `sweetspot-managed-bootstrap-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

const inputClass =
  'rounded-md border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink ' +
  'focus:border-amp-olive focus:outline-none dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';

/**
 * The enrollment link, shown once, as a QR code.
 *
 * The QR is the point. The link carries a 43-character one-time token, so
 * nobody is going to type it on a tablet keyboard, and it is never retrievable
 * afterwards. Scanning it with the tablet's camera is the whole enrollment step.
 *
 * The code is rendered in the browser (the `qrcode` package, to a data URL), so
 * the token never travels to a third-party QR service. That matters: whoever
 * generates the image can enroll the tablet.
 *
 * Copy stays for the cases a camera cannot cover: a tablet whose camera is
 * broken, or an operator who would rather paste the link into the tablet's
 * browser over remote support.
 */
function EnrollLinkCard({ url, onDismiss }: { url: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [qrFailed, setQrFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setQr(null);
    setQrFailed(false);
    // Error correction M and a wide quiet zone: the tablet is scanning a screen,
    // often at an angle, sometimes with glare.
    toDataURL(url, { errorCorrectionLevel: 'M', margin: 2, width: 220 })
      .then((data) => {
        if (alive) setQr(data);
      })
      .catch(() => {
        if (alive) setQrFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [url]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="mb-3 rounded-md border border-amp-olive bg-amp-olive/5 px-3 py-3 dark:border-amp-olive-dark dark:bg-amp-olive-dark/10">
      <p className="mb-1 text-sm font-medium text-amp-ink dark:text-amp-dark-ink">
        Scan this with the tablet's camera. It works one time and it is not shown again.
      </p>
      <p className="mb-3 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Open the tablet's Camera app and point it at the code, then tap the link it offers. The
        tablet lands on the store home screen when it works. If the code is lost or expires, use
        Reissue on the row for a fresh one.
      </p>
      <div className="flex flex-wrap items-start gap-4">
        <div className="shrink-0 rounded-md bg-white p-2">
          {qr ? (
            <img src={qr} alt="Enrollment QR code" width={220} height={220} />
          ) : (
            <div className="flex h-[220px] w-[220px] items-center justify-center text-xs text-amp-ink-muted">
              {qrFailed ? 'Could not draw the code. Use Copy instead.' : 'Drawing the code...'}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Or paste the link into the tablet's browser:
          </p>
          <code className="mb-2 block break-all rounded bg-amp-white px-2 py-1.5 text-xs text-amp-ink dark:bg-amp-dark-surface dark:text-amp-dark-ink">
            {url}
          </code>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copy}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-amp-olive px-3 py-1.5 text-sm font-medium text-white dark:bg-amp-olive-dark"
            >
              {copied ? (
                <Check className="h-4 w-4" aria-hidden />
              ) : (
                <Copy className="h-4 w-4" aria-hidden />
              )}
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="shrink-0 rounded-md border border-amp-line px-3 py-1.5 text-sm text-amp-ink hover:border-amp-olive dark:border-amp-dark-line dark:text-amp-dark-ink"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ d }: { d: KioskDeviceRow }) {
  if (d.status === 'revoked') return <Badge tone="danger">Revoked</Badge>;
  if (d.status === 'active') return <Badge tone="success">Active</Badge>;
  if (d.enroll_pending) return <Badge tone="warn">Link outstanding</Badge>;
  return <Badge tone="neutral">Link expired</Badge>;
}

function DeviceTr({
  d,
  onLink,
  selected,
  onSelectedChange,
}: {
  d: KioskDeviceRow;
  onLink: (url: string) => void;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
}) {
  const reissueM = useReissueKioskEnrollment();
  const revokeM = useRevokeKioskDevice();
  const busy = reissueM.isPending || revokeM.isPending;

  const reissue = () => {
    reissueM.mutate({ id: d.id }, { onSuccess: (r) => onLink(r.enroll_url) });
  };
  const revoke = () => {
    if (
      !window.confirm(
        `Revoke the tablet at ${d.store_name ?? d.store_no}? It loses access on its next request, and getting it back needs a new enrollment link.`,
      )
    )
      return;
    revokeM.mutate({ id: d.id });
  };

  return (
    <tr className="align-top">
      <td className="w-[42px] px-3 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={(event) => onSelectedChange(event.target.checked)}
          disabled={d.status !== 'active' || !d.serial}
          aria-label={`Select ${d.label ?? d.serial ?? `tablet ${d.id}`} for managed app setup`}
          title={
            d.status !== 'active'
              ? 'The tablet must be active'
              : !d.serial
                ? 'Add a serial number before managed app setup'
                : 'Include this tablet in the managed app setup export'
          }
          className="h-4 w-4 accent-amp-olive disabled:opacity-40"
        />
      </td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        <span className="font-medium">{d.store_name ?? `Store ${d.store_no}`}</span>
        <span className="ml-1 text-amp-ink-muted dark:text-amp-dark-ink-muted">({d.store_no})</span>
        {d.store_account && (
          <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {d.store_account}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        {d.label ?? <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">No name</span>}
        {(d.serial || d.snipe_asset_tag) && (
          <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {[d.serial, d.snipe_asset_tag && `asset ${d.snipe_asset_tag}`].filter(Boolean).join(' - ')}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <StatusBadge d={d} />
      </td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        {sinceLabel(d.last_seen_at)}
        {d.last_seen_ip && (
          <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {d.last_seen_ip}
            {d.last_seen_off_network ? ' (off the store network)' : ''}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={reissue}
            disabled={busy || d.status === 'revoked'}
            title="Issue a new one-time enrollment link"
            className="inline-flex items-center gap-1 rounded-md border border-amp-line px-2 py-1 text-xs text-amp-ink hover:border-amp-olive disabled:opacity-40 dark:border-amp-dark-line dark:text-amp-dark-ink"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Reissue
          </button>
          <button
            type="button"
            onClick={revoke}
            disabled={busy || d.status === 'revoked'}
            title="Cut this tablet off on its next request"
            className="inline-flex items-center gap-1 rounded-md border border-amp-line px-2 py-1 text-xs text-red-700 hover:border-red-400 disabled:opacity-40 dark:border-amp-dark-line dark:text-red-400"
          >
            <Ban className="h-3.5 w-3.5" aria-hidden /> Revoke
          </button>
        </div>
        {(reissueM.error || revokeM.error) && (
          <p className="mt-1 text-xs text-red-700 dark:text-red-400">
            {(reissueM.error ?? revokeM.error)?.message}
          </p>
        )}
      </td>
    </tr>
  );
}

export function StoreTablets() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.tablets', user?.superAdmin);

  const [filterStore, setFilterStore] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = parseSortParam(searchParams.get('sort'));
  const sortBy = sort?.column ?? null;
  const sortDir = sort?.dir ?? 'asc';
  const toggleSort = (col: SortColumn) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (sortBy !== col) next.set('sort', `${col}:asc`);
        else if (sortDir === 'asc') next.set('sort', `${col}:desc`);
        else next.delete('sort');
        return next;
      },
      { replace: true },
    );
  };

  const devicesQ = useKioskDevices({
    storeNo: filterStore || undefined,
    status: filterStatus || undefined,
  });
  const scoreQ = useScorecard(denverToday()); // active-store list for the picker
  const createM = useCreateKioskDevice();
  const bootstrapM = useManagedKioskBootstrap();

  const [storeNo, setStoreNo] = useState('');
  const [label, setLabel] = useState('');
  const [serial, setSerial] = useState('');
  const [assetTag, setAssetTag] = useState('');
  const [enrollUrl, setEnrollUrl] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [bootstrapMessage, setBootstrapMessage] = useState<string | null>(null);

  if (user && !canView) return <AccessDeniedCard what="Store tablet administration" />;
  if (isNotAvailable(devicesQ.error)) {
    return (
      <div>
        <StoresHeader title="Store tablets" />
        <NotAvailableCard what="Store tablet administration" />
      </div>
    );
  }

  const stores = [...(scoreQ.data?.stores ?? [])].sort((a, b) => a.store_no - b.store_no);
  const rawDevices = devicesQ.data?.devices ?? [];
  const devices = sortBy ? sortDevices(rawDevices, sortBy, sortDir) : rawDevices;
  const eligibleDevices = devices.filter((device) => device.status === 'active' && device.serial);
  const selectedDevices = rawDevices.filter(
    (device) => selectedIds.has(device.id) && device.status === 'active' && device.serial,
  );
  const allEligibleSelected =
    eligibleDevices.length > 0 && eligibleDevices.every((device) => selectedIds.has(device.id));

  const setDeviceSelected = (id: number, selected: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (selected) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const exportManagedBootstrap = () => {
    const deviceIds = selectedDevices.map((device) => device.id);
    if (!deviceIds.length) return;
    setBootstrapMessage(null);
    bootstrapM.mutate(
      { device_ids: deviceIds },
      {
        onSuccess: (result) => {
          downloadManagedBootstrap(result);
          setBootstrapMessage(
            `Downloaded ${result.devices.length} one-time token${result.devices.length === 1 ? '' : 's'}. Merge by Serial_No into Scalefusion's custom-field CSV.`,
          );
          setSelectedIds(new Set());
        },
      },
    );
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    createM.mutate(
      {
        store_no: storeNo,
        ...(label.trim() ? { label: label.trim() } : {}),
        ...(serial.trim() ? { serial: serial.trim() } : {}),
        ...(assetTag.trim() ? { snipe_asset_tag: assetTag.trim() } : {}),
      },
      {
        onSuccess: (d) => {
          setLabel('');
          setSerial('');
          setAssetTag('');
          setEnrollUrl(d.enroll_url);
        },
      },
    );
  };

  const addForm = (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
      <select
        value={storeNo}
        onChange={(e) => setStoreNo(e.target.value)}
        required
        aria-label="Store"
        className={inputClass}
      >
        <option value="" disabled>
          Store…
        </option>
        {stores.map((s) => (
          <option key={s.store_no} value={String(s.store_no)}>
            {storeLabel(s.store_no, s.store_name)}
          </option>
        ))}
      </select>
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Name, e.g. Back office tablet"
        aria-label="Tablet name"
        className={`${inputClass} min-w-[180px]`}
      />
      <input
        value={serial}
        onChange={(e) => setSerial(e.target.value)}
        placeholder="Serial"
        aria-label="Serial number"
        className={inputClass}
      />
      <input
        value={assetTag}
        onChange={(e) => setAssetTag(e.target.value)}
        placeholder="Asset tag"
        aria-label="Snipe-IT asset tag"
        className={`${inputClass} w-[110px]`}
      />
      <button
        type="submit"
        disabled={createM.isPending || !storeNo}
        className="inline-flex items-center gap-1.5 rounded-md bg-amp-olive px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-amp-olive-dark"
      >
        <TabletSmartphone className="h-4 w-4" aria-hidden /> Register
      </button>
      <button
        type="button"
        onClick={exportManagedBootstrap}
        disabled={bootstrapM.isPending || selectedDevices.length === 0}
        title="Download one-time managed-app tokens for the selected active tablets"
        className="inline-flex items-center gap-1.5 rounded-md border border-amp-line px-3 py-1.5 text-sm font-medium text-amp-ink hover:border-amp-olive disabled:opacity-40 dark:border-amp-dark-line dark:text-amp-dark-ink"
      >
        {bootstrapM.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <Download className="h-4 w-4" aria-hidden />
        )}
        Export app setup
      </button>
      {/* Most-severe-first, per the filter-ordering standard. */}
      <select
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        aria-label="Filter by status"
        className={`${inputClass} ml-auto`}
      >
        <option value="">All statuses</option>
        <option value="revoked">Revoked</option>
        <option value="pending">Awaiting enrollment</option>
        <option value="active">Active</option>
      </select>
      <select
        value={filterStore}
        onChange={(e) => setFilterStore(e.target.value)}
        aria-label="Filter by store"
        className={inputClass}
      >
        <option value="">All stores</option>
        {stores.map((s) => (
          <option key={s.store_no} value={String(s.store_no)}>
            {storeLabel(s.store_no, s.store_name)}
          </option>
        ))}
      </select>
    </form>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <StoresHeader
        title="Store tablets"
        subtitle="Register a back-of-house tablet so it stays inside The Sweet Spot without anyone signing in. Revoking here cuts the tablet off on its next request."
      />
      {enrollUrl && <EnrollLinkCard url={enrollUrl} onDismiss={() => setEnrollUrl(null)} />}
      {createM.error && (
        <div className="mb-3">
          <ErrorCard message={createM.error.message} />
        </div>
      )}
      {bootstrapM.error && (
        <div className="mb-3">
          <ErrorCard message={bootstrapM.error.message} />
        </div>
      )}
      {bootstrapMessage ? (
        <p className="mb-3 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {bootstrapMessage}
        </p>
      ) : null}
      <DataGridShell total={devices.length} loaded={devices.length} countNoun="tablets" toolbar={addForm}>
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="w-[42px] px-3 py-2 text-left">
                <input
                  type="checkbox"
                  checked={allEligibleSelected}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setSelectedIds((current) => {
                      const next = new Set(current);
                      for (const device of eligibleDevices) {
                        if (checked) next.add(device.id);
                        else next.delete(device.id);
                      }
                      return next;
                    });
                  }}
                  disabled={eligibleDevices.length === 0}
                  aria-label="Select all active tablets with serial numbers"
                  className="h-4 w-4 accent-amp-olive disabled:opacity-40"
                />
              </th>
              <SortableHeader
                label="Store"
                sorted={sortBy === 'store' ? sortDir : null}
                onToggle={() => toggleSort('store')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Tablet"
                sorted={sortBy === 'label' ? sortDir : null}
                onToggle={() => toggleSort('label')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Status"
                sorted={sortBy === 'status' ? sortDir : null}
                onToggle={() => toggleSort('status')}
                thClassName="px-3 py-2 w-[150px]"
              />
              <SortableHeader
                label="Last seen"
                sorted={sortBy === 'last_seen' ? sortDir : null}
                onToggle={() => toggleSort('last_seen')}
                thClassName="px-3 py-2 w-[170px]"
              />
              <th className="px-3 py-2 w-[190px] text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {devicesQ.isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading tablets...
                </td>
              </tr>
            ) : devicesQ.error ? (
              <tr>
                <td colSpan={6} className="px-4 py-6">
                  <ErrorCard message={devicesQ.error.message} />
                </td>
              </tr>
            ) : devices.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No tablets registered yet. Pick a store above and register one.
                </td>
              </tr>
            ) : (
              devices.map((d) => (
                <DeviceTr
                  key={d.id}
                  d={d}
                  onLink={setEnrollUrl}
                  selected={selectedIds.has(d.id)}
                  onSelectedChange={(selected) => setDeviceSelected(d.id, selected)}
                />
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}
